import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * PageHeaderTrailContext — Round 22 (owner feedback).
 *
 * The page banner (DashboardPageHeader) publishes its title + breadcrumb
 * trail here; the fixed topbar (Layouts/Header.js) renders it. Result:
 * the page name and its clickable trail stay visible while the page
 * scrolls — on every module and sub-page, automatically — instead of
 * scrolling away with the in-page banner.
 *
 * Pages render banners exactly as before; no per-page wiring needed.
 * Pages without a banner simply leave the topbar slot empty.
 */
const PageHeaderTrailContext = createContext({ trail: null, setTrail: () => {}, leaves: {}, setLeaf: () => {} });

export const PageHeaderTrailProvider = ({ children }) => {
    const [trail, setTrail] = useState(null); // { title, crumbs: [{label, to?, onClick?}] }
    // Owner 2026-10-12: crumbs appended by whatever is open INSIDE the page
    // (a job in Digital Inspection, a document in its viewer, an EPO, a ticket),
    // keyed by the view that registered them, ordered by `order`.
    const [leaves, setLeaves] = useState({});
    const setLeaf = useCallback((id, entry) => {
        setLeaves((prev) => {
            if (!entry) {
                if (!(id in prev)) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
            }
            return { ...prev, [id]: entry };
        });
    }, []);
    return (
        <PageHeaderTrailContext.Provider value={{ trail, setTrail, leaves, setLeaf }}>
            {children}
        </PageHeaderTrailContext.Provider>
    );
};

export const usePageHeaderTrail = () => useContext(PageHeaderTrailContext);

/**
 * useTrailLeaf — owner 2026-10-12 ("do the same navigation in every module").
 *
 * A view that opens inside a page appends its crumb(s) to the topbar trail
 * while it is open: Digital Inspection > Job 12345 > Op 10 > DNA 3, or
 * EPO > EPO #4711. `crumbs` = [{ label, onClick? }] or null when nothing is
 * open; `order` sorts leaves from several views (outer view = lower number).
 * onClick handlers are read live, so the registration only changes with the
 * labels.
 */
let leafSeq = 0;
export const useTrailLeaf = (crumbs, order = 0) => {
    const { setLeaf } = useContext(PageHeaderTrailContext);
    const idRef = useRef(null);
    if (idRef.current == null) idRef.current = ++leafSeq;
    const latest = useRef(crumbs);
    latest.current = crumbs;
    const list = Array.isArray(crumbs) ? crumbs.filter(Boolean) : [];
    const sig = list.map((c) => `${c.label}|${c.to || ""}|${c.onClick ? "fn" : ""}`).join(">");
    useEffect(() => {
        const id = idRef.current;
        if (!sig) {
            setLeaf(id, null);
            return undefined;
        }
        const stable = (latest.current || []).filter(Boolean).map((c, i) => ({
            label: c.label,
            to: c.to,
            onClick: c.onClick
                ? () => { const live = (latest.current || []).filter(Boolean)[i]; if (live && live.onClick) live.onClick(); }
                : undefined,
        }));
        setLeaf(id, { order, crumbs: stable });
        return () => setLeaf(id, null);
    }, [sig, order, setLeaf]);
};

/**
 * TrailPrefixContext — owner 2026-10-12 ("this navigation should also change").
 *
 * A module whose screens live under ONE route (MAFIA Aux: menu > All Parts >
 * Edit Part) publishes the crumbs that sit between "Dashboard" and the screen
 * it renders: [{ label: "MAFIA Aux", onClick }, { label: "All Parts", onClick }].
 * DashboardPageHeader puts them in front of the screen's own last crumb, so the
 * topbar reads Dashboard > MAFIA Aux > All Parts > Edit Part, every crumb live.
 */
const TrailPrefixContext = createContext(null);

export const TrailPrefixProvider = ({ crumbs, children }) => (
    <TrailPrefixContext.Provider value={crumbs || null}>{children}</TrailPrefixContext.Provider>
);

export const useTrailPrefix = () => useContext(TrailPrefixContext);

export default PageHeaderTrailContext;
